import * as React from 'react';
import { memo } from 'react';

import { Autocomplete } from '@emburse/ui-core';

import { ICompanyCategoryModel } from '@Shared/models';

type CategorySelectorProps = {
  categories: ICompanyCategoryModel[];
  selectedCategoryId?: string;
  selectedCategory?: ICompanyCategoryModel;
  onCategorySelect: (v: ICompanyCategoryModel) => any;
  loading?: boolean;
  disabled?: boolean;
};

const getCategoryLabel = (category: ICompanyCategoryModel) => category.name;

const CategorySelector = ({ categories, selectedCategoryId, onCategorySelect, loading, disabled }: CategorySelectorProps) => {
  const flattenedCategories = React.useMemo(
    () =>
      categories.reduce((accumulator: ICompanyCategoryModel[], category) => {
        if (category.subcategories) {
          accumulator.push(
            ...category.subcategories.map((subcategory: ICompanyCategoryModel) => ({
              ...subcategory,
              name: `${category.name} / ${subcategory.name}`
            }))
          );
        } else {
          accumulator.push(category);
        }
        return accumulator;
      }, []),
    [categories]
  );

  let selected = flattenedCategories.find(({ id }) => id === selectedCategoryId) || null;
  if (disabled) {
    selected = null;
  }

  React.useEffect(() => {
    // Clear out selected category if it is not included within provided options.
    //  Setting new selection by value property does not trigger onChange event
    //  so we need to do it manually.
    if (!loading && selectedCategoryId !== selected?.id && categories.length) {
      onCategorySelect(null);
    }
  }, [categories, selectedCategoryId]);

  return (
    <Autocomplete
      label="Category"
      value={selected}
      options={flattenedCategories}
      getOptionLabel={getCategoryLabel}
      onChange={(_: React.ChangeEvent<HTMLSelectElement>, value: ICompanyCategoryModel) => onCategorySelect(value)}
      disabled={disabled}
    />
  );
};

const MemoizedCategorySelector = memo(CategorySelector);

export { MemoizedCategorySelector as CategorySelector };
